"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import Button from "@/components/common/Button";
import FullView from "@/components/common/FullView";
import { PATH } from "@/constants/path";
import { useBatchImpressionTracking } from "@/hooks/useImpressionTracking";
import { useScrollDepthTracking } from "@/hooks/useScrollDepthTracking";
import { trackEvent } from "@/utils/analytics";
import { cn } from "@/utils/cn";

// 섹션 컨테이너
const sectionClasses = cn(
  "relative flex flex-col",
  "items-center justify-center",
  "bg-gradient-to-b from-primary/10 to-transparent",
  "px-side py-16",
  "min-h-0",
);

// 텍스트 + 버튼 컨테이너
const contentContainerClasses = cn("z-10 flex flex-col gap-8", "items-center", "w-full max-w-[800px]");

// 타이틀
const titleClasses = cn("text-center text-36 font-semibold", "tablet:text-40");

// 버튼 영역
const buttonGroupClasses = cn("flex flex-wrap gap-x-4 gap-y-3", "justify-center");

// 이미지 컨테이너
const imageContainerClasses = cn(
  "relative w-full max-w-[800px]",
  "mt-8 tablet:mt-12 desktop:mt-16",
  "overflow-hidden rounded-t-2xl shadow-lg",
  "aspect-[1765/988]",
  "flex-shrink-0",
);

function HeroSection() {
  useScrollDepthTracking();

  const path = usePathname();

  const impressionEvents = useMemo(
    () => [
      { eventName: "cta_impression", params: { cta_type: "inquiry", page_location: "/service/", cta_position: "top" } },
      {
        eventName: "download_btn_impression",
        params: { file_name: "company_deck", page_location: "/service/", cta_position: "top" },
      },
    ],
    [],
  );

  const heroRef = useBatchImpressionTracking<HTMLDivElement>(impressionEvents);

  const handleInquiryClick = () => {
    trackEvent("cta_click", path, { cta_type: "inquiry", page_location: "/service/", cta_position: "top" });
  };

  const handleDownloadClick = () => {
    trackEvent("file_download", path, { file_name: "company_deck", page_location: "/service/", cta_position: "top" });
  };

  return (
    <FullView as="section" className={sectionClasses}>
      <div ref={heroRef} className={contentContainerClasses}>
        <div className={titleClasses}>
          <span className="whitespace-nowrap">핵심 인재 채용,</span>
          <br className="block" />
          <span className="whitespace-nowrap">리스트 발굴부터</span> <span className="whitespace-nowrap">채용 진행까지</span>{" "}
          <span className="whitespace-nowrap">한 번에!</span>
        </div>
        <div className={buttonGroupClasses}>
          <Button href={PATH.REQUEST} onClick={handleInquiryClick}>
            문의하기
          </Button>
          <Button href={PATH.COMPANY_INTRO} color="gray" target="_blank" rel="noopener noreferrer"
                  onClick={handleDownloadClick}>
            회사 소개서 다운받기
          </Button>
        </div>
      </div>

      <div className={imageContainerClasses}>
        <Image
          src="/image/service/list.png"
          alt="list"
          fill
          sizes="(max-width: 768px) 100vw, 800px"
          priority
          quality={90}
          className="object-cover"
        />
      </div>
    </FullView>
  );
}

export default HeroSection;
